import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Profile, ProfileInput } from 'teamProfiles/_types';
import { APIKey, apiRoutes } from 'constants/apiKeys';
import baseFetcher from 'shared/helpers/baseFetcher';

export interface Department {
  id: string;
  name: string;
}

export function useProfiles() {
  return useQuery<Profile[]>({
    queryKey: [APIKey.PROFILES],
    queryFn: () => baseFetcher(apiRoutes.profiles()),
  });
}

export function useProfile(id?: string) {
  return useQuery<Profile>({
    queryKey: [APIKey.PROFILES, { id }],
    queryFn: () => baseFetcher(apiRoutes.profile(id)),
    enabled: !!id,
  });
}

export function useDepartments() {
  return useQuery<Department[]>({
    queryKey: [APIKey.DEPARTMENTS],
    queryFn: () => baseFetcher(apiRoutes.departments()),
  });
}

export function useUpdateProfileMutation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<ProfileInput>) =>
      baseFetcher(apiRoutes.profile(id), { method: 'PATCH', data }),
    onSuccess: (data) => {
      queryClient.setQueryData([APIKey.PROFILES, { id }], () => data);
      queryClient.setQueryData([APIKey.PROFILES], (oldData: Profile[]) =>
        oldData?.map((profile) => (profile.id === id ? data : profile))
      );
    },
  });
}

interface CreateProfileData extends Omit<ProfileInput, 'id'> {
  entity_extraction_id?: string;
}

export function useCreateProfileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: CreateProfileData) =>
      baseFetcher(apiRoutes.profiles(), { method: 'POST', data }),
    onSuccess: (data) => {
      queryClient.setQueryData([APIKey.PROFILES, { id: data.id }], () => data);
      queryClient.setQueryData([APIKey.PROFILES], (oldData: Profile[]) => [
        ...oldData,
        data,
      ]);
    },
  });
}

export function useDeleteProfileMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) =>
      baseFetcher(apiRoutes.profile(id), { method: 'DELETE' }),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: [APIKey.PROFILES, { id }] });
      queryClient.setQueryData([APIKey.PROFILES], (oldData: Profile[]) =>
        oldData?.filter((profile) => profile.id !== id)
      );
    },
  });
}
